import React, { useEffect, useState } from "react";
import { useChannelHistoryQuery } from "../../../Services/ticket/ChannelHistory";
import LoadingSpinner from "../../LoadingSpinner";
import {
  getBadgeClasses,
  getIconClasses,
} from "../../reusableComponent/IconAndBadgeClasses";
import { useNavigate } from "react-router-dom";
import Pagination from "@mui/material/Pagination";
import { PaginationItem } from "@mui/material";

interface IProps {
  tickets: any;
  refetch: any;
}

const ChannelHistory: React.FC<IProps> = ({ tickets, refetch }) => {
  const [displayedData, setDisplayedData] = useState<any[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [ticketHistoryPage, setTicketHistoryPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const { data, status: channelHistoryStatus } = useChannelHistoryQuery({
    id: tickets.action.contact.id,
    page: ticketHistoryPage,
  });

  const navigate = useNavigate();

  const formatDate = (timestamp: string) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getShortDate = (timestamp: string) => {
    if (!timestamp) return "N/A";
    
    const date = new Date(timestamp);
    const now = new Date();
    
    // Reset time to midnight for accurate day comparison
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const diffTime = nowOnly.getTime() - dateOnly.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays > 0 && diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    setTicketHistoryPage(page);
  };

  useEffect(() => {
    if (data) {
      setTotalPages(Math.ceil(data.count / 5));
      setDisplayedData(data.results);
    }
  }, [data]);

  useEffect(() => {
    setIsLoadingHistory(channelHistoryStatus === "pending");
  }, [channelHistoryStatus]);

  const displayedItems = showAll ? displayedData : displayedData?.slice(0, 5);

  if (isLoadingHistory) {
    return (
      <div className="text-center py-3">
        <LoadingSpinner />
      </div>
    );
  }

  if (!displayedData || displayedData.length === 0) {
    return (
      <div className="history-empty-state">
        <div className="empty-icon-circle">
          <i className="bi bi-clock-history"></i>
        </div>
        <p className="empty-text">No history available</p>
      </div>
    );
  }

  return (
    <div className="channel-history-modern">
      <div className="history-timeline">
        {displayedItems?.map((item: any, index: number) => (
          <div
            key={index}
            className={`timeline-item ${
              hoveredIndex === index ? "hovered" : ""
            }`}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            onClick={() =>
              navigate(
                `/ticket/information/${
                  item?.ticket?.id ? item?.ticket?.id : item?.ref_id
                }`,
              )
            }
          >
            {/* Timeline Track */}
            <div className="timeline-track">
              <div
                className={`timeline-dot ${getBadgeClasses(
                  item?.ticket?.ticket_channel || "Email",
                )}`}
              >
                <i
                  className={getIconClasses(
                    item?.ticket?.ticket_channel || "Email",
                  )}
                ></i>
              </div>
              {index !== displayedItems.length - 1 && (
                <div className="timeline-line"></div>
              )}
            </div>

            {/* Content Card */}
            <div className="timeline-card">
              {/* Quick Info Bar */}
              <div className="card-quick-info">
                <div className="info-left">
                  <span className="channel-type">
                    {item?.ticket?.ticket_channel || "Email"}
                  </span>
                  <span className="separator">•</span>
                  <span className="time-short">
                    {getShortDate(item?.created)}
                  </span>
                </div>
                <div className="info-right">
                  <span className="ticket-id">
                    #{item?.ticket?.id || item?.ref_id}
                  </span>
                  <i className="bi bi-chevron-right arrow-icon"></i>
                </div>
              </div>

              {/* Main Content - Only shows on hover or mobile */}
              <div className="card-expandable-content">
                {item.message && (
                  <div className="content-message">
                    <p>{item.message}</p>
                  </div>
                )}

                {item?.ticket?.description && (
                  <div className="content-description">
                    <p>{item.ticket.description}</p>
                  </div>
                )}

                <div className="content-footer">
                  <div className="footer-item">
                    <i className="bi bi-calendar3"></i>
                    <span>
                      {formatDate(item?.created)}
                    </span>
                  </div>
                  {item?.assignee?.username && (
                    <div className="footer-item assignee">
                      <i className="bi bi-person"></i>
                      <span>{item.assignee.username}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Show More */}
      {displayedData?.length > 5 && (
        <div className="history-show-more">
          <button
            className="show-more-button"
            onClick={(e) => {
              e.stopPropagation();
              setShowAll(!showAll);
            }}
          >
            <i
              className={`bi ${showAll ? "bi-chevron-up" : "bi-chevron-down"}`}
            ></i>
            <span>
              {showAll ? "Show Less" : `Show ${displayedData.length - 5} More`}
            </span>
          </button>
        </div>
      )}

      {/* Pagination */}
      <div
        className="d-flex flex-row-reverse mt-4 mb-4"
        style={{ marginRight: "20px" }}
      >
        <Pagination
          sx={{
            display: "flex",
            justifyContent: "end",
            "& .MuiPaginationItem-root": {
              fontSize: "14px",
              minWidth: "36px",
              height: "36px",
              margin: "0 2px",
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
              color: "#374151",
              "&:hover": {
                backgroundColor: "#f9fafb",
                borderColor: "#d1d5db",
              },
              "&.Mui-selected": {
                backgroundColor: "#6366f1",
                color: "#ffffff",
                borderColor: "#6366f1",
                "&:hover": {
                  backgroundColor: "#4f46e5",
                  borderColor: "#4f46e5",
                },
              },
            },
          }}
          count={totalPages}
          page={ticketHistoryPage}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
          renderItem={(item) => {
            if (item.type === "page") {
              const isNearCurrent =
                Math.abs(item.page - ticketHistoryPage) <= 1 ||
                item.page === 1 ||
                item.page === totalPages;
              return isNearCurrent ? <PaginationItem {...item} /> : null;
            }
            return <PaginationItem {...item} />;
          }}
        />
      </div>
    </div>
  );
};

export default ChannelHistory;